import { axiosInstance } from "authProvider";
import { API_URL } from "../../constants";

export interface CreditReportParams {
  from?: string;
  to?: string;
  groupBy?: 'day' | 'month' | 'client' | 'type';
  clientId?: number;
  status?: 'paid' | 'unpaid';
  type?: 'credit' | 'debit';
  page?: number;
  pageSize?: number;
  /** When true, API returns all groups/rows (no pagination) for print/PDF */
  forExport?: boolean;
}

export interface CreditTransaction {
  id: number;
  date: string;
  clientId: number;
  clientName: string;
  clientPseudo?: string;
  clientPhone?: string | number;
  amount: number;
  type: 'credit' | 'debit';
  status: 'paid' | 'unpaid';
  paidAmount: number;
  paidAt?: string;
  paidBy?: {
    id: number;
    username: string;
  };
  remainingBalance: number;
  creditEntryId: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreditReportSummary {
  totalTransactions: number;
  totalAmount: number;
  paidCount: number;
  unpaidCount: number;
  creditAmount: number;
  /** Sum of amounts for credit-type lines marked paid */
  paidCreditAmount?: number;
  /** Sum of amounts for credit-type lines still unpaid */
  unpaidCreditAmount?: number;
  debitAmount: number;
  netAmount: number;
  clientCount: number;
  typeBreakdown: {
    credit: number;
    debit: number;
  };
}

export interface CreditReportResponse {
  summary: CreditReportSummary;
  data: CreditTransaction[] | any[];
  pagination?: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

export const payCredit = (
  credit_v2_id: string | number, 
  credit_transaction_id: number, 
  payment_amount: number
) => async (): Promise<any> => {
  return await axiosInstance
    .post(`${API_URL}/api/credit-v2/pay-credit`, { 
      credit_v2_id, 
      credit_transaction_id,
      payment_amount
    })
    .then(({ data }) => data);
};

export const getMonths = async (): Promise<any> => {
  return await axiosInstance
    .get(`${API_URL}/api/credit-v2/months`)
    .then((data) => data);
};

export const searchCreditsByClient = async (
  client_name: string, 
  month: string
) => {
  return await axiosInstance
    .get(`${API_URL}/api/credit-v2/search-client-transactions`, {
      params: {
        client_name,
        month
      }
    })
    .then((data) => data);
};

export const processMultipleTransactions = async (transactions: Array<{
  id: number;
  amount: number;
  credit_v2_id: number;
  client: {
    id: number;
    name: string;
  }
}>) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/api/credit-v2/process-multiple-transactions`, { transactions });
    return response.data;
  } catch (error) {
    console.error('Error processing multiple transactions:', error);
    throw error;
  }
};

export const getCreditReports = async (params: CreditReportParams): Promise<CreditReportResponse> => {
  try {
    const response = await axiosInstance.get(`${API_URL}/api/credit-v2/reports`, {
      params
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching credit reports:', error);
    throw error;
  }
};

/**
 * Delete a specific credit transaction
 * @param transactionId - ID of the credit transaction to delete
 */
export const deleteTransaction = async (transactionId: number): Promise<any> => {
  try {
    const response = await axiosInstance.delete(`${API_URL}/api/credit-v2/transaction/${transactionId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting credit transaction:', error);
    throw error;
  }
};

/**
 * Delete an entire credit entry and all its transactions
 * @param creditId - ID of the credit entry to delete
 */
export const deleteCredit = async (creditId: number): Promise<any> => {
  try {
    const response = await axiosInstance.delete(`${API_URL}/api/credit-v2/${creditId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting credit entry:', error);
    throw error;
  }
};

/**
 * Delete all credits for the current user
 * @returns Promise with the result of the operation
 */
export const deleteAllCredits = async (): Promise<any> => {
  try {
    const response = await axiosInstance.delete(`${API_URL}/api/credit-v2/delete-all`);
    return response.data;
  } catch (error) {
    console.error('Error deleting all credits:', error);
    throw error;
  }
};

/**
 * Update a specific credit transaction
 * @param transactionId - ID of the credit transaction to update
 * @param amount - New amount for the transaction
 * @param type - Optional new type for the transaction ('credit' or 'debit')
 */
export const updateTransaction = async (
  transactionId: number, 
  amount: number, 
  type?: 'credit' | 'debit'
): Promise<any> => {
  try {
    const response = await axiosInstance.put(`${API_URL}/api/credit-v2/transaction/${transactionId}`, {
      amount,
      ...(type && { type })
    });
    return response.data;
  } catch (error) {
    console.error('Error updating credit transaction:', error);
    throw error;
  }
};
